import { useCallback, useEffect, useState } from 'react';

import { useWindowSize } from 'react-use';

import { TOCItem } from '../../types';

type Offset = {
  id: string;
  offset: number;
};

export const useActiveTOCHeading = (
  imagesLoaded: boolean,
  contentRef: React.MutableRefObject<HTMLDivElement | null>,
  items: TOCItem[],
): string => {
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [offsets, setOffsets] = useState<Offset[]>([]);
  const { width, height } = useWindowSize();

  // go through the content and get the offsets of each heading
  useEffect(() => {
    const heads = contentRef?.current?.querySelectorAll<HTMLElement>(
      [1, ...items.map(heading => heading.depth)]
        .map(depth => `h${depth}`)
        .toString(),
    );

    if (heads) {
      setOffsets(
        Array.from(heads)
          .map(heading => {
            const anchor = heading.querySelector('a');
            if (!anchor) return null;

            const offset = heading.querySelector('span')?.offsetTop;

            return {
              id: heading.id,
              offset: heading.offsetTop + (offset || 0),
            };
          })
          .filter(Boolean) as Offset[],
      );
    }
  }, [width, height, contentRef, imagesLoaded, items]);

  // the scroll handler will determine which heading is active
  const handleScroll = useCallback(() => {
    Object.values(offsets).forEach(({ id, offset }) => {
      if (window.screenY && window.scrollY + 200 >= offset) {
        setActiveHeading(id);
      }
    });
  }, [offsets]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return activeHeading;
};
